import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import TopBar from "../components/TopBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminDemoPage — Admin UI to manage the public Demo Player Development.
 * Route: /admin/demo. Requires admin password (from localStorage).
 */
export default function AdminDemoPage() {
  const navigate = useNavigate();
  const password = localStorage.getItem("st_admin_pw") || "";
  const headers = { "X-Admin-Password": password };

  const [cfg, setCfg] = useState(null);
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState({ type: null, text: "" });

  const loadAll = useCallback(async () => {
    try {
      const [c, v] = await Promise.all([
        axios.get(`${API}/admin/demo/config`, { headers }),
        axios.get(`${API}/admin/demo/videos`, { headers }),
      ]);
      setCfg(c.data);
      setVideos(v.data);
    } catch (e) {
      if (e?.response?.status === 401) navigate("/admin");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (!password) navigate("/admin");
    loadAll();
  }, [loadAll, navigate, password]);

  if (!cfg) {
    return (
      <div className="min-h-screen bg-[#080808] text-white">
        <TopBar />
        <div className="max-w-4xl mx-auto p-10 text-neutral-500">…</div>
      </div>
    );
  }

  const setField = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const saveConfig = async () => {
    setBusy(true);
    setMsg({ type: null, text: "" });
    try {
      const r = await axios.put(`${API}/admin/demo/config`, cfg, { headers });
      setCfg(r.data);
      setMsg({ type: "success", text: "✓ Guardado" });
      setTimeout(() => setMsg({ type: null, text: "" }), 2500);
    } catch (e) {
      setMsg({ type: "error", text: e?.response?.data?.detail || "Error" });
    } finally {
      setBusy(false);
    }
  };

  const flash = (t, ok = true) => {
    setMsg({ type: ok ? "success" : "error", text: t });
    setTimeout(() => setMsg({ type: null, text: "" }), 3000);
  };

  const uploadVideo = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const file = fd.get("file");
    if (!file || file.size === 0) return;
    if (file.size > 500 * 1024 * 1024) {
      flash("El archivo excede 500MB", false);
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/demo/videos`, fd, {
        headers: { ...headers, "Content-Type": "multipart/form-data" },
      });
      e.target.reset();
      await loadAll();
      flash("✓ Video subido");
    } catch (err) {
      flash(err?.response?.data?.detail || "Upload error", false);
    } finally {
      setBusy(false);
    }
  };

  const deleteVideo = async (id) => {
    if (!window.confirm("¿Eliminar este video del demo?")) return;
    await axios.delete(`${API}/admin/demo/videos/${id}`, { headers });
    await loadAll();
    flash("✓ Video eliminado");
  };

  const updateTimelineEntry = (idx, patch) => {
    const t = [...(cfg.timeline || [])];
    t[idx] = { ...t[idx], ...patch };
    setField("timeline", t);
  };

  const addTimelineEntry = () => {
    const t = [...(cfg.timeline || [])];
    t.push({
      label_en: "TRAINING",
      label_es: "ENTRENAMIENTO",
      week_en: `Week ${t.length + 1}`,
      week_es: `Semana ${t.length + 1}`,
      skill_en: "",
      skill_es: "",
      notes_en: "",
      notes_es: "",
      video_id: null,
    });
    setField("timeline", t);
  };

  const removeTimelineEntry = (idx) => {
    const t = [...(cfg.timeline || [])];
    t.splice(idx, 1);
    setField("timeline", t);
  };

  const updateSkillBar = (idx, patch) => {
    const bars = [...(cfg.skill_bars || [])];
    bars[idx] = { ...bars[idx], ...patch };
    setField("skill_bars", bars);
  };

  const addSkillBar = () => {
    const bars = [...(cfg.skill_bars || [])];
    bars.push({ name_en: "New Skill", name_es: "Nueva habilidad", value: 60 });
    setField("skill_bars", bars);
  };

  const removeSkillBar = (idx) => {
    const bars = [...(cfg.skill_bars || [])];
    bars.splice(idx, 1);
    setField("skill_bars", bars);
  };

  const updateLibraryCat = (idx, patch) => {
    const cats = [...(cfg.library_categories || [])];
    cats[idx] = { ...cats[idx], ...patch };
    setField("library_categories", cats);
  };
  const addLibraryCat = () => {
    const cats = [...(cfg.library_categories || [])];
    cats.push({ en: "New Category", es: "Nueva categoría" });
    setField("library_categories", cats);
  };
  const removeLibraryCat = (idx) => {
    const cats = [...(cfg.library_categories || [])];
    cats.splice(idx, 1);
    setField("library_categories", cats);
  };

  return (
    <div className="min-h-screen bg-[#080808] text-white">
      <TopBar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-[#e8559a] transition-colors mb-4"
        >
          ← Admin
        </Link>

        <div className="rounded-2xl bg-gradient-to-br from-[#161616] to-[#1a0e16] border border-[#e8559a]/30 p-5 sm:p-8 mb-6">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#e8559a]/80 mb-2">
            DEMO PLAYER PROFILE
          </div>
          <h1 className="font-bebas text-4xl sm:text-5xl tracking-wider">
            Manage Public Demo
          </h1>
          <p className="text-sm text-neutral-400 mt-2 max-w-2xl">
            Este contenido aparece en la página pública y en el perfil de ejemplo <code className="text-[#e8559a]">/demo</code>. Nadie necesita registrarse para verlo.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <a
              href="/demo"
              target="_blank"
              rel="noreferrer"
              className="px-4 py-2 rounded-lg bg-[#0f0f0f] border border-[#252525] hover:border-[#e8559a]/50 text-sm text-neutral-200 transition-colors"
              data-testid="admin-demo-view-preview"
            >
              👁 Ver /demo →
            </a>
            <button
              onClick={saveConfig}
              disabled={busy}
              data-testid="admin-demo-save-btn"
              className="px-5 py-2 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] disabled:opacity-50 text-white font-bebas tracking-widest text-sm transition-colors"
            >
              💾 {busy ? "..." : "Guardar cambios"}
            </button>
          </div>
        </div>

        {msg.text && (
          <div
            className={`mb-4 rounded-xl p-3 text-sm ${
              msg.type === "success"
                ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-300"
                : "bg-[#e05555]/10 border border-[#e05555]/40 text-[#e05555]"
            }`}
          >
            {msg.text}
          </div>
        )}

        {/* Videos library */}
        <Section title="📹 Videos Subidos">
          <p className="text-xs text-neutral-500 mb-3">
            Sube videos aquí y luego selecciónalos abajo para las tarjetas Antes/Después y para cada evento del timeline.
          </p>
          <form
            onSubmit={uploadVideo}
            data-testid="admin-demo-upload-form"
            className="mb-4 rounded-xl bg-[#0f0f0f] border border-[#252525] p-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                name="title"
                required
                placeholder="Título del video"
                data-testid="admin-demo-video-title"
                className="bg-[#161616] border border-[#252525] rounded-lg px-3 py-2 text-white text-sm focus:border-[#e8559a] focus:outline-none"
              />
              <input
                name="label"
                placeholder="Etiqueta (before / after / week_2)"
                data-testid="admin-demo-video-label"
                className="bg-[#161616] border border-[#252525] rounded-lg px-3 py-2 text-white text-sm focus:border-[#e8559a] focus:outline-none"
              />
              <input
                name="file"
                type="file"
                accept="video/*"
                required
                data-testid="admin-demo-video-file"
                className="text-neutral-300 text-sm file:mr-3 file:px-3 file:py-2 file:rounded-md file:border-0 file:bg-[#e8559a] file:text-white file:font-medium"
              />
            </div>
            <div className="mt-3 flex justify-end">
              <button
                type="submit"
                disabled={busy}
                data-testid="admin-demo-upload-btn"
                className="px-5 py-2 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] disabled:opacity-60 text-white font-bebas tracking-widest text-sm transition-colors"
              >
                ⬆ {busy ? "..." : "Subir video"}
              </button>
            </div>
          </form>
          {videos.length === 0 ? (
            <div className="text-sm text-neutral-500 text-center py-4">
              Ningún video subido aún. Los slots del timeline se verán como &quot;Coming soon&quot;.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {videos.map((v) => (
                <div
                  key={v.id}
                  data-testid={`admin-demo-video-${v.id}`}
                  className="rounded-xl bg-[#0f0f0f] border border-[#252525] p-3"
                >
                  <video
                    src={`${API}/demo/videos/${v.id}/file`}
                    controls
                    preload="metadata"
                    className="w-full aspect-video rounded-md bg-black mb-2"
                  />
                  <div className="text-sm font-medium text-white truncate">
                    {v.title}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-[#e8559a]/80 mt-0.5">
                    {v.label || "—"}
                  </div>
                  <div className="text-[10px] text-neutral-500 mt-0.5 truncate">
                    ID: {v.id}
                  </div>
                  <button
                    onClick={() => deleteVideo(v.id)}
                    className="mt-2 w-full px-2 py-1.5 rounded text-xs bg-[#e05555]/10 border border-[#e05555]/40 text-[#e05555] hover:bg-[#e05555]/20 transition-colors"
                  >
                    🗑 Eliminar
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Landing text */}
        <Section title="📝 Texto en la página pública (landing)">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextArea label="Headline (ES)" value={cfg.landing_headline_es} onChange={(v) => setField("landing_headline_es", v)} rows={3} />
            <TextArea label="Headline (EN)" value={cfg.landing_headline_en} onChange={(v) => setField("landing_headline_en", v)} rows={3} />
            <TextArea label="Subtext (ES)" value={cfg.landing_subtext_es} onChange={(v) => setField("landing_subtext_es", v)} rows={3} />
            <TextArea label="Subtext (EN)" value={cfg.landing_subtext_en} onChange={(v) => setField("landing_subtext_en", v)} rows={3} />
          </div>
        </Section>

        {/* Sample player */}
        <Section title="👤 Sample Player (Perfil de ejemplo)">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <TextField label="Nombre" value={cfg.sample_player_name} onChange={(v) => setField("sample_player_name", v)} />
            <TextField label="Edad" type="number" value={cfg.sample_player_age} onChange={(v) => setField("sample_player_age", parseInt(v, 10) || 0)} />
            <TextField label="Posición (ES)" value={cfg.sample_player_position_es} onChange={(v) => setField("sample_player_position_es", v)} />
            <TextField label="Posición (EN)" value={cfg.sample_player_position_en} onChange={(v) => setField("sample_player_position_en", v)} />
            <TextField label="Skill (ES)" value={cfg.skill_es} onChange={(v) => setField("skill_es", v)} />
            <TextField label="Skill (EN)" value={cfg.skill_en} onChange={(v) => setField("skill_en", v)} />
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextArea label="Coach feedback (ES)" value={cfg.coach_feedback_es} onChange={(v) => setField("coach_feedback_es", v)} rows={2} />
            <TextArea label="Coach feedback (EN)" value={cfg.coach_feedback_en} onChange={(v) => setField("coach_feedback_en", v)} rows={2} />
            <TextArea label="Next goal (ES)" value={cfg.next_goal_es} onChange={(v) => setField("next_goal_es", v)} rows={2} />
            <TextArea label="Next goal (EN)" value={cfg.next_goal_en} onChange={(v) => setField("next_goal_en", v)} rows={2} />
            <TextArea label="Fortalezas (ES)" value={cfg.strengths_es} onChange={(v) => setField("strengths_es", v)} rows={2} />
            <TextArea label="Strengths (EN)" value={cfg.strengths_en} onChange={(v) => setField("strengths_en", v)} rows={2} />
            <TextArea label="Áreas a mejorar (ES)" value={cfg.areas_to_improve_es} onChange={(v) => setField("areas_to_improve_es", v)} rows={2} />
            <TextArea label="Areas to improve (EN)" value={cfg.areas_to_improve_en} onChange={(v) => setField("areas_to_improve_en", v)} rows={2} />
          </div>
        </Section>

        {/* Before / After Video Selection */}
        <Section title="🎬 Video Antes / Después">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <VideoPicker
              label="◀ Antes / Before"
              videos={videos}
              value={cfg.before_video_id}
              onChange={(id) => setField("before_video_id", id)}
              week={cfg.before_date_label}
              onWeekChange={(v) => setField("before_date_label", v)}
              testid="admin-demo-before-picker"
            />
            <VideoPicker
              label="▶ Después / After"
              videos={videos}
              value={cfg.after_video_id}
              onChange={(id) => setField("after_video_id", id)}
              week={cfg.after_date_label}
              onWeekChange={(v) => setField("after_date_label", v)}
              testid="admin-demo-after-picker"
            />
          </div>
        </Section>

        {/* Skill Bars */}
        <Section
          title="📊 Indicadores de coaching (barras)"
          right={
            <button
              onClick={addSkillBar}
              data-testid="admin-demo-add-skill"
              className="px-3 py-1.5 rounded-md bg-[#e8559a]/10 border border-[#e8559a]/40 hover:bg-[#e8559a]/20 text-[#e8559a] text-xs transition-colors"
            >
              ＋ Nueva barra
            </button>
          }
        >
          <div className="space-y-2">
            {(cfg.skill_bars || []).map((b, i) => (
              <div
                key={i}
                className="rounded-lg bg-[#0f0f0f] border border-[#252525] p-3 grid grid-cols-1 sm:grid-cols-4 gap-2 items-center"
              >
                <input
                  className="bg-[#161616] border border-[#252525] rounded px-2 py-1.5 text-sm text-white focus:border-[#e8559a] focus:outline-none"
                  value={b.name_es || ""}
                  placeholder="Nombre (ES)"
                  onChange={(e) => updateSkillBar(i, { name_es: e.target.value })}
                />
                <input
                  className="bg-[#161616] border border-[#252525] rounded px-2 py-1.5 text-sm text-white focus:border-[#e8559a] focus:outline-none"
                  value={b.name_en || ""}
                  placeholder="Name (EN)"
                  onChange={(e) => updateSkillBar(i, { name_en: e.target.value })}
                />
                <input
                  type="number"
                  min={0}
                  max={100}
                  className="bg-[#161616] border border-[#252525] rounded px-2 py-1.5 text-sm text-white focus:border-[#e8559a] focus:outline-none"
                  value={b.value ?? 0}
                  onChange={(e) => updateSkillBar(i, { value: parseInt(e.target.value, 10) || 0 })}
                />
                <button
                  onClick={() => removeSkillBar(i)}
                  className="px-2 py-1.5 rounded bg-[#e05555]/10 border border-[#e05555]/40 text-[#e05555] text-xs hover:bg-[#e05555]/20"
                >
                  🗑
                </button>
              </div>
            ))}
          </div>
        </Section>

        {/* Timeline */}
        <Section
          title="📅 Timeline de Entrenamiento"
          right={
            <button
              onClick={addTimelineEntry}
              data-testid="admin-demo-add-timeline"
              className="px-3 py-1.5 rounded-md bg-[#e8559a]/10 border border-[#e8559a]/40 hover:bg-[#e8559a]/20 text-[#e8559a] text-xs transition-colors"
            >
              ＋ Nueva entrada
            </button>
          }
        >
          <div className="space-y-3">
            {(cfg.timeline || []).map((e, i) => (
              <div
                key={i}
                data-testid={`admin-demo-timeline-${i}`}
                className="rounded-xl bg-[#0f0f0f] border border-[#252525] p-4"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#e8559a]/80">
                    Entrada #{i + 1}
                  </div>
                  <button
                    onClick={() => removeTimelineEntry(i)}
                    className="px-2 py-1 rounded bg-[#e05555]/10 border border-[#e05555]/40 text-[#e05555] text-[10px] hover:bg-[#e05555]/20"
                  >
                    🗑 Eliminar
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-2">
                  <input className="bg-[#161616] border border-[#252525] rounded px-2 py-1.5 text-sm text-white focus:border-[#e8559a] focus:outline-none" value={e.label_es || ""} placeholder="Etiqueta ES (ANTES/ENTRENAMIENTO/DESPUÉS)" onChange={(ev) => updateTimelineEntry(i, { label_es: ev.target.value })} />
                  <input className="bg-[#161616] border border-[#252525] rounded px-2 py-1.5 text-sm text-white focus:border-[#e8559a] focus:outline-none" value={e.label_en || ""} placeholder="Label EN (BEFORE/TRAINING/AFTER)" onChange={(ev) => updateTimelineEntry(i, { label_en: ev.target.value })} />
                  <input className="bg-[#161616] border border-[#252525] rounded px-2 py-1.5 text-sm text-white focus:border-[#e8559a] focus:outline-none" value={e.week_es || ""} placeholder="Semana ES" onChange={(ev) => updateTimelineEntry(i, { week_es: ev.target.value })} />
                  <input className="bg-[#161616] border border-[#252525] rounded px-2 py-1.5 text-sm text-white focus:border-[#e8559a] focus:outline-none" value={e.week_en || ""} placeholder="Week EN" onChange={(ev) => updateTimelineEntry(i, { week_en: ev.target.value })} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                  <input className="bg-[#161616] border border-[#252525] rounded px-2 py-1.5 text-sm text-white focus:border-[#e8559a] focus:outline-none" value={e.skill_es || ""} placeholder="Skill ES" onChange={(ev) => updateTimelineEntry(i, { skill_es: ev.target.value })} />
                  <input className="bg-[#161616] border border-[#252525] rounded px-2 py-1.5 text-sm text-white focus:border-[#e8559a] focus:outline-none" value={e.skill_en || ""} placeholder="Skill EN" onChange={(ev) => updateTimelineEntry(i, { skill_en: ev.target.value })} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
                  <textarea rows={2} className="bg-[#161616] border border-[#252525] rounded px-2 py-1.5 text-sm text-white focus:border-[#e8559a] focus:outline-none resize-none" value={e.notes_es || ""} placeholder="Notas ES" onChange={(ev) => updateTimelineEntry(i, { notes_es: ev.target.value })} />
                  <textarea rows={2} className="bg-[#161616] border border-[#252525] rounded px-2 py-1.5 text-sm text-white focus:border-[#e8559a] focus:outline-none resize-none" value={e.notes_en || ""} placeholder="Notes EN" onChange={(ev) => updateTimelineEntry(i, { notes_en: ev.target.value })} />
                </div>
                <VideoSelector
                  videos={videos}
                  value={e.video_id}
                  onChange={(id) => updateTimelineEntry(i, { video_id: id })}
                  testid={`admin-demo-timeline-video-${i}`}
                />
              </div>
            ))}
          </div>
        </Section>

        {/* Library */}
        <Section
          title="📚 Librería de Videos (categorías)"
          right={
            <button
              onClick={addLibraryCat}
              className="px-3 py-1.5 rounded-md bg-[#e8559a]/10 border border-[#e8559a]/40 hover:bg-[#e8559a]/20 text-[#e8559a] text-xs transition-colors"
            >
              ＋ Categoría
            </button>
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {(cfg.library_categories || []).map((c, i) => (
              <div key={i} className="rounded-lg bg-[#0f0f0f] border border-[#252525] p-3 grid grid-cols-3 gap-2 items-center">
                <input className="bg-[#161616] border border-[#252525] rounded px-2 py-1.5 text-sm text-white focus:border-[#e8559a] focus:outline-none" value={c.es || ""} placeholder="Nombre ES" onChange={(e) => updateLibraryCat(i, { es: e.target.value })} />
                <input className="bg-[#161616] border border-[#252525] rounded px-2 py-1.5 text-sm text-white focus:border-[#e8559a] focus:outline-none" value={c.en || ""} placeholder="Name EN" onChange={(e) => updateLibraryCat(i, { en: e.target.value })} />
                <button onClick={() => removeLibraryCat(i)} className="px-2 py-1.5 rounded bg-[#e05555]/10 border border-[#e05555]/40 text-[#e05555] text-xs hover:bg-[#e05555]/20">🗑</button>
              </div>
            ))}
          </div>
        </Section>

        {/* Sticky bottom save */}
        <div className="sticky bottom-4 mt-8 flex justify-end">
          <button
            onClick={saveConfig}
            disabled={busy}
            data-testid="admin-demo-save-btn-bottom"
            className="px-6 py-3 rounded-xl bg-[#e8559a] hover:bg-[#c9407f] disabled:opacity-60 text-white font-bebas tracking-widest transition-colors shadow-lg shadow-[#e8559a]/40"
          >
            💾 {busy ? "..." : "Guardar todos los cambios"}
          </button>
        </div>
      </main>
    </div>
  );
}

// ============ Reusable admin UI helpers ============
function Section({ title, right, children }) {
  return (
    <section className="mb-6 rounded-2xl bg-[#161616] border border-[#252525] p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-bebas text-2xl tracking-widest text-white">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

function TextField({ label, value, onChange, type = "text" }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-neutral-500 mb-1">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0f0f0f] border border-[#252525] rounded-lg px-3 py-2 text-white text-sm focus:border-[#e8559a] focus:outline-none"
      />
    </label>
  );
}

function TextArea({ label, value, onChange, rows = 3 }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-neutral-500 mb-1">{label}</span>
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#0f0f0f] border border-[#252525] rounded-lg px-3 py-2 text-white text-sm focus:border-[#e8559a] focus:outline-none resize-none"
      />
    </label>
  );
}

function VideoPicker({ label, videos, value, onChange, week, onWeekChange, testid }) {
  const selected = videos.find((v) => v.id === value);
  return (
    <div data-testid={testid} className="rounded-xl bg-[#0f0f0f] border border-[#252525] p-4">
      <div className="text-[10px] uppercase tracking-widest text-[#e8559a]/80 mb-2">{label}</div>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full bg-[#161616] border border-[#252525] rounded-lg px-3 py-2 text-white text-sm focus:border-[#e8559a] focus:outline-none"
      >
        <option value="">— Ningún video —</option>
        {videos.map((v) => (
          <option key={v.id} value={v.id}>
            {v.title} {v.label ? `· ${v.label}` : ""}
          </option>
        ))}
      </select>
      <input
        placeholder="Etiqueta de semana (Week 1)"
        value={week || ""}
        onChange={(e) => onWeekChange(e.target.value)}
        className="w-full mt-2 bg-[#161616] border border-[#252525] rounded-lg px-3 py-2 text-white text-sm focus:border-[#e8559a] focus:outline-none"
      />
      {selected && (
        <video
          src={`${API}/demo/videos/${selected.id}/file`}
          controls
          preload="metadata"
          className="w-full aspect-video rounded-md bg-black mt-3"
        />
      )}
    </div>
  );
}

function VideoSelector({ videos, value, onChange, testid }) {
  return (
    <div>
      <span className="block text-[10px] uppercase tracking-wider text-neutral-500 mb-1">Video</span>
      <select
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value || null)}
        className="w-full bg-[#161616] border border-[#252525] rounded-lg px-3 py-2 text-white text-sm focus:border-[#e8559a] focus:outline-none"
      >
        <option value="">— Sin video —</option>
        {videos.map((v) => (
          <option key={v.id} value={v.id}>
            {v.title} {v.label ? `· ${v.label}` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}
